import { useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import { AppScreen, type AppJob } from '@/components/brand/CrewlineApp';
import { DayStamp, Incoming, NotificationBanner, Outgoing, PhoneFrame, SmsComposeBar, SmsHeader, SmsThread, TypingBubble } from '@/components/brand/PhoneConversation';
import { useBranding } from '@/lib/branding';
import { useMediaQuery } from '@/lib/hooks';
import { cn } from '@/lib/utils';

/**
 * The animated product story that flanks the sign-in card on wide screens.
 *
 * One clock drives two phones on a shared 3D stage: a worker's phone on the left ("what the crew
 * sees") and the Crewline app on a phone on the right ("what you see"). Each scenario plays the
 * same beats: the notice is typed in the app (or drafted by a weather rule), sent, lands on the
 * worker's phone under a notification banner, the delivery bar fills with the language split, the
 * worker texts a keyword, the app logs it and the automatic reply lands. Then everything fades and the next scenario starts; the loop runs forever. All state
 * is derived from the elapsed time, so nothing can drift out of step.
 *
 * The phones sit on a shared perspective (set on the grid) with a gentle tilt toward the card, a
 * slow float and a soft ground shadow; the pointer nudges the tilt a few degrees through `--mx`/`--my`
 * written straight to the stage elements, never through React state. Restraint on purpose: the
 * story is on the screens.
 *
 * Copy mirrors what the product really sends: sender-label prefix, blank line, opt-out footer,
 * keyword replies with the label and no footer. Under `prefers-reduced-motion` the first scenario is
 * shown finished, without the clock, the float or the parallax.
 *
 * Renders two grid items placed in columns 1 and 3 (the card is column 2); hidden below `xl`.
 */

const JOB: AppJob = { name: 'Orlando Tower', number: '(407) 555-0142', contacts: 48 };
const FOOTER = 'Reply STOP to opt out.';
const LANGUAGES = [
  ['EN', 33],
  ['ES', 13],
  ['PT', 2],
] as const;
/** Typing speed in the app, ms per character. */
const TYPE_MS = 24;
/** Silence between scenarios after the fade-out. */
const GAP_MS = 500;

interface Scenario {
  key: string;
  /** Present when a weather rule drafted and sent the notice. */
  rule?: string;
  body: string;
  keyword: string;
  from: string;
  reply: string;
  delivered: number;
  skipped: number;
}

const SCENARIOS: Scenario[] = [
  {
    key: 'gate',
    body: 'Gate B is closed Thursday for crane work. Use the Church St entrance and badge in by 6:30 AM.',
    keyword: 'PARKING',
    from: '(407) 555-0119',
    reply: 'Parking this week is the Church St lot, north side. The Gate B lot is closed through Friday.',
    delivered: 46,
    skipped: 2,
  },
  {
    key: 'weather',
    rule: 'lightning within 8 miles',
    body: 'Lightning within 8 miles of the site. Stop exterior work and shelter in place until you get the all-clear.',
    keyword: 'SAFETY',
    from: '(407) 555-0187',
    reply: 'Safety office is in trailer 2, ext. 114. Report any incident right away.',
    delivered: 48,
    skipped: 0,
  },
  {
    key: 'pour',
    body: 'Concrete pour moved up to 5:30 AM tomorrow. Be on site by 5:00 and check in with Aaron at the north gate.',
    keyword: 'HOURS',
    from: '(407) 555-0163',
    reply: 'Site hours are Mon–Sat, 6:30 AM to 4:00 PM. Pour days start at 5:00 AM.',
    delivered: 47,
    skipped: 1,
  },
];

/**
 * Times on the phones follow the viewer's own clock: the status bar shows the current local time, the notice
 * is stamped with the moment it was "sent" in this run of the scene and the crew's reply with when it landed,
 * all in the viewer's locale (12- or 24-hour). `status` drops the AM/PM the way a phone's status bar does.
 */
const CLOCK = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });
function localTime(at: number): { status: string; full: string } {
  const d = new Date(at);
  const status = CLOCK.formatToParts(d)
    .filter((p) => p.type !== 'dayPeriod' && !(p.type === 'literal' && p.value.trim() === ''))
    .map((p) => p.value)
    .join('')
    .trim();
  return { status, full: CLOCK.format(d) };
}

/** Beat times (ms from the start of the scenario). */
interface Beats {
  typeStart: number;
  typeEnd: number;
  send: number;
  receive: number;
  bannerEnd: number;
  deliverEnd: number;
  kwType: number;
  kwSend: number;
  inbound: number;
  typing: number;
  reply: number;
  fade: number;
  end: number;
}

function beats(s: Scenario): Beats {
  const typeStart = 500;
  const typeEnd = s.rule ? typeStart + 1100 : typeStart + s.body.length * TYPE_MS;
  const send = typeEnd + 700;
  const receive = send + 700;
  const bannerEnd = receive + 2800;
  const deliverEnd = receive + 1500;
  const kwType = deliverEnd + 1100;
  const kwSend = kwType + 220 * s.keyword.length + 500;
  const inbound = kwSend + 500;
  const typing = kwSend + 800;
  const reply = kwSend + 2200;
  const end = reply + 4200;
  return { typeStart, typeEnd, send, receive, bannerEnd, deliverEnd, kwType, kwSend, inbound, typing, reply, fade: end - 600, end };
}

function useLoopClock(total: number, enabled: boolean): number {
  const [t, setT] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    const start = performance.now();
    const id = window.setInterval(() => setT((performance.now() - start) % total), 40);
    return () => window.clearInterval(id);
  }, [total, enabled]);
  return t;
}

const clamp01 = (n: number) => (n < 0 ? 0 : n > 1 ? 1 : n);

export function SignInDemo() {
  const { branding } = useBranding();
  const reduced = useMediaQuery('(prefers-reduced-motion: reduce)');
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);

  const timeline = useMemo(() => {
    let offset = 0;
    return SCENARIOS.map((s) => {
      const b = beats(s);
      const entry = { s, b, offset };
      offset += b.end + GAP_MS;
      return entry;
    });
  }, []);
  const total = timeline[timeline.length - 1]!.offset + timeline[timeline.length - 1]!.b.end + GAP_MS;
  const t = useLoopClock(total, !reduced);

  const current = reduced ? timeline[0]! : (timeline.find((e) => t < e.offset + e.b.end + GAP_MS) ?? timeline[0]!);
  const { s, b } = current;
  // Reduced motion: hold the first scenario at its finished state.
  const tl = reduced ? b.reply + 1 : t - current.offset;

  // Pointer parallax, written to CSS variables on both stages.
  useEffect(() => {
    if (reduced) return;
    let raf = 0;
    const onMove = (e: PointerEvent) => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const mx = ((e.clientX / window.innerWidth) * 2 - 1).toFixed(3);
        const my = ((e.clientY / window.innerHeight) * 2 - 1).toFixed(3);
        for (const el of [leftRef.current, rightRef.current]) {
          el?.style.setProperty('--mx', mx);
          el?.style.setProperty('--my', my);
        }
      });
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => {
      window.removeEventListener('pointermove', onMove);
      cancelAnimationFrame(raf);
    };
  }, [reduced]);

  // ---- derived state -------------------------------------------------------------------------
  const dim = tl >= b.fade;
  const typedChars = s.rule ? (tl >= b.typeStart + 500 ? s.body.length : 0) : Math.min(s.body.length, Math.max(0, Math.floor((tl - b.typeStart) / TYPE_MS)));
  const draft = s.body.slice(0, typedChars);
  const typing = !s.rule && tl >= b.typeStart && tl < b.typeEnd;
  const phase: 'compose' | 'sending' | 'sent' = tl < b.send ? 'compose' : tl < b.receive ? 'sending' : 'sent';
  const progress = clamp01((tl - b.receive) / (b.deliverEnd - b.receive));
  const rendered = `${JOB.name}: ${s.body}\n\n${FOOTER}`;
  const kwChars = Math.min(s.keyword.length, Math.max(0, Math.floor((tl - b.kwType) / 220)));
  const phoneDraft = tl >= b.kwType && tl < b.kwSend ? s.keyword.slice(0, kwChars) : '';
  // Wall-clock moments for this run of the scene, so every timestamp agrees with the viewer's own clock.
  const startedAt = Date.now() - tl;
  const sentAt = localTime(startedAt + b.send);
  const inboundAt = localTime(startedAt + b.inbound);
  const clock = localTime(startedAt + tl).status;
  const inbound = tl >= b.inbound ? { keyword: s.keyword, from: s.from, at: inboundAt.full, replied: tl >= b.reply } : null;

  return (
    <>
      <Stage side="left" label="What the crew sees" ref={leftRef}>
        <PhoneFrame
          time={clock}
          className={FADE_IN}
          banner={tl >= b.receive && tl < b.bannerEnd ? <NotificationBanner title={JOB.name} body={`${JOB.name}: ${s.body}`} /> : null}
        >
          <SmsHeader name={JOB.name} number={JOB.number} />
          <SmsThread dim={dim}>
            {tl >= b.receive ? (
              <>
                <DayStamp>Today {sentAt.full}</DayStamp>
                <Incoming>{rendered}</Incoming>
              </>
            ) : null}
            {tl >= b.kwSend ? <Outgoing receipt={tl >= b.kwSend + 500 ? 'Delivered' : undefined}>{s.keyword}</Outgoing> : null}
            {tl >= b.typing && tl < b.reply ? <TypingBubble /> : null}
            {tl >= b.reply ? <Incoming>{`${JOB.name}: ${s.reply}`}</Incoming> : null}
          </SmsThread>
          <SmsComposeBar draft={phoneDraft} />
        </PhoneFrame>
      </Stage>

      <Stage side="right" label="What you see" ref={rightRef}>
        <PhoneFrame time={clock} className={FADE_IN}>
          <AppScreen
            appName={branding.appName}
            job={JOB}
            draft={draft}
            typing={typing}
            rendered={rendered}
            rule={s.rule ? (tl >= b.typeStart ? s.rule : null) : null}
            phase={phase}
            sentAt={sentAt.full}
            progress={progress}
            delivered={s.delivered}
            skipped={s.skipped}
            languages={LANGUAGES}
            inbound={inbound}
            dim={dim}
          />
        </PhoneFrame>
      </Stage>
    </>
  );
}

/** Fade for the devices themselves (kept off the 3D ancestors: opacity below 1 would flatten them). */
const FADE_IN = 'motion-safe:animate-in motion-safe:fade-in-0 fill-mode-both duration-1000';

/**
 * A grid cell holding one phone: a small label, then the device floating slowly over its ground
 * shadow, turned a few degrees toward the card. The grid supplies the perspective, so both phones
 * share one vanishing point; `--mx`/`--my` (−1..1) from the parallax effect add up to three degrees.
 */
function Stage({ side, label, ref, children }: { side: 'left' | 'right'; label: string; ref: RefObject<HTMLDivElement | null>; children: ReactNode }) {
  const ry = side === 'left' ? -8 : 8;
  return (
    <div
      className={cn(
        'hidden xl:flex xl:flex-col xl:items-center xl:gap-5 xl:row-start-1 xl:pt-1 [transform-style:preserve-3d]',
        side === 'left' ? 'xl:col-start-1' : 'xl:col-start-3',
        'motion-safe:animate-in motion-safe:slide-in-from-bottom-8 fill-mode-both duration-1000',
        side === 'left' ? '[animation-delay:100ms]' : '[animation-delay:300ms]',
      )}
      aria-hidden
    >
      <p className={cn('text-muted-foreground flex items-center gap-2.5 text-[10px] font-medium tracking-[0.18em] uppercase', FADE_IN)}>
        <span className="bg-brand-accent size-1.5" />
        {label}
      </p>
      <div ref={ref} className={cn('anim-float relative [transform-style:preserve-3d]', side === 'right' && '[animation-delay:-3.5s]')}>
        <div className="pointer-events-none absolute -bottom-9 left-1/2 h-9 w-[70%] rounded-full bg-black/20 blur-2xl dark:bg-black/70" style={{ transform: 'translateX(calc(-50% + var(--mx, 0) * -12px)) translateZ(-40px)' }} />
        <div
          className="transition-transform duration-500 ease-out will-change-transform [transform-style:preserve-3d]"
          style={{ transform: `rotateY(calc(${ry}deg + var(--mx, 0) * 3deg)) rotateX(calc(var(--my, 0) * -2deg))` }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
